<script setup lang="ts">
type Msg = 'In stock' | 'Remove' | 'Subscribe' | 'Close' | 'Update' | 'Add'

const t2 = useT2({
  'In stock': 'Yra sandėlyje',
  Subscribe: 'Prenumeruoti',
  Add: 'Pridėti',
})

defineProps<{
  active: boolean
  images: gen.ImageFragment[] | None
  label: string
  count: number | None | 'off'
  sup?: string | None
  disabled?: boolean
  btnColor?: 'black' | 'red' | 'grey'
  msg: Msg
  submit(): NotVoid
}>()

const notify = useNotify()
</script>

<template>
  <button :disabled type="button" :class="[!disabled && 'ButtonIcon', $style.button, active && $style.active]" @click.passive="notify.watch(submit())">
    <span v-if="images?.length || $slots.icon" :class="[$style.imgCont, $slots.icon && $style.icon]">
      <slot name="icon">
        <BaseImage v-for="(img, index) in images" :key="index" :img="img" sizes="120px" ratio="R4x5" :class="[$style.img, `v${images?.length}${index}`]" />
      </slot>
    </span>

    <span :class="$style.txt2">
      <span :class="$style.txt">
        <span>{{ label }}</span>
        <sup v-if="count !== 'off'">{{ helpers.zeroPad(count ?? 0) }}</sup>
      </span>
      <span v-if="sup?.length" :class="$style.sup">{{ sup }}</span>
      <slot />
    </span>

    <ButtonIcon :txt="t2(msg)" size="36" to="span">
      <span :class="[$style.save, btnColor == 'black' ? $style.saveBlack : btnColor == 'red' ? $style.saveRed : btnColor == 'grey' ? $style.saveGrey : undefined]">
        <IconCheck v-if="msg === 'Remove' || msg === 'In stock'" class="_svg14" />
        <IconPlus v-else-if="msg === 'Add' || msg === 'Subscribe'" class="_svg14" />
        <IconChevron v-else-if="msg === 'Close' || msg === 'Update'" :open="msg === 'Close'" size="_svg14" />

        <span v-if="notify.notify.value.isLoading" :class="$style.loading">
          <UiLoading />
        </span>
      </span>
    </ButtonIcon>
  </button>
</template>

<style module>
.loading {
  composes: global(_ctr);
  composes: global(_absolute);
  composes: global(_inset);

  composes: global(_bg1);
  composes: global(_c0);
  z-index: 1;
}
.button {
  composes: global(_row);
  composes: global(_fs09);
  composes: global(_fw-l);
  composes: global(_ls-xs);
  composes: global(_ctrY);
  composes: global(_h50);
  composes: global(_w100p);
  composes: global(_br14);

  composes: global(_pt04);
  composes: global(_pr04);
  composes: global(_pb04);
  composes: global(_pl04);

  composes: global(_spaced);
  composes: global(_bg0);

  --shadow: 3px 5px 3px hsla(240, 4%, 10%, 0.05), 2px 3px 10px hsla(0, 0%, 50%, 0.05), 0px 0px 3px rgba(0, 0, 0, 0.1), 3px 5px 0px 2px hsl(240, 50%, 99.5%, 0.05);
  box-shadow: var(--shadow);
}
.button:not(:disabled):hover {
  background: var(--blue5);
  box-shadow:
    var(--shadow),
    0 0 0 1px var(--blue7);
}

.save {
  composes: global(_hidden);
  composes: global(_relative);
  composes: global(_br99);

  composes: global(_ctr);

  composes: global(_ls-xs);
  composes: global(_fw-xl);
  composes: global(_fs09);

  color: var(--save);
  background: var(--saveBg);

  composes: global(_h28);
  composes: global(_w28);
}
.button:hover .save {
  background: var(--saveHovBg);
  color: var(--saveHov);
}
.saveRed,
.button {
  --save: var(--black);
  --saveBg: var(--black-5);
  --saveHov: var(--bg);
  --saveHovBg: red;
}
.button:not(.active):not(:disabled) {
  --save: var(--black-2);
  --saveBg: var(--black-5);
  --saveHov: var(--bg);
  --saveHovBg: red;
}
.saveBlack,
.active {
  --save: var(--bg);
  --saveBg: var(--black-2);
  --saveHov: var(--bg);
  --saveHovBg: var(--black);
}
.saveGrey {
  --save: var(--black);
  --saveBg: var(--black-5);
  --saveHov: var(--black);
  --saveHovBg: var(--bg);
}

.button:disabled {
  cursor: not-allowed;
  --save: var(--black-4);
  --saveBg: var(--black-5);
  --saveHov: var(--black-4);
  --saveHovBg: var(--black-5);
}

.txt2 {
  composes: global(_wmin0);

  align-self: flex-start;
  flex: 9999 1 auto;
}
.txt {
  composes: global(_row);
  composes: global(_gap04);
  composes: global(_lh12);
  composes: global(_fw-xl);
  composes: global(_pt04);
  composes: global(_pr1);
  composes: global(_pl2);
  composes: global(_hidden);

  word-break: break-word;
  composes: global(_c2);
}
.sup {
  composes: global(_pl2);
  composes: global(_ff3);
  composes: global(_fs08);
  composes: global(_lh1);
  composes: global(_fw-xl);
}

.imgCont {
  composes: global(_relative);
  composes: global(_ctr);
  composes: global(_h100p);
  composes: global(_w50);
}
.icon {
  background: var(--black-6);
  composes: global(_br9);
}

.button:not(:disabled):hover .icon {
  background: var(--blue3);
  color: var(--bg);
  --fill: var(--black);
}
.img {
  composes: global(_absolute);
  composes: global(_w28);
  composes: global(_br6);

  top: 0;
  left: 0;
  transform: translate(10px, 0px);
}
.img:first-of-type {
  transform: translate(0, 2px) rotate(-15deg);
}
.img:last-of-type {
  transform: translate(20px, 2px) rotate(15deg);
}

.button:disabled .txt {
  color: var(--black-4);
}
</style>
